import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';
import { InviteForm } from './AdminMembersPage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS: Record<string, { label: string; cls: string }> = {
  active: { label: 'Actief', cls: 'bg-emerald-100 text-emerald-700' },
  suspended: { label: 'Geschorst', cls: 'bg-amber-100 text-amber-700' },
  blocked: { label: 'Geblokkeerd', cls: 'bg-red-100 text-red-700' },
  inactive: { label: 'Wacht op goedkeuring', cls: 'bg-slate-100 text-slate-600' },
};
const ACTIE: Record<string, string> = { active: 'reactivate', suspended: 'suspend', blocked: 'block' };

export const AdminLedenPage: React.FC = () => {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };
  const notify = useToast();

  const [colls, setColls] = useState<any[]>([]);
  const [cid, setCid] = useState(sp.get('collectivity') || '');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    fetch(`${API_BASE_URL}/api/settlement/collectivities-list`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then((d) => { setColls(d); if (!cid && d[0]) setCid(d[0].id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken]);

  const load = useCallback(() => {
    if (!cid) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/admin/toegankelijkheid?collectivity_id=${cid}`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then(setRows).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, accessToken]);
  useEffect(() => { load(); if (cid) setSp({ collectivity: cid }, { replace: true }); }, [load]); // eslint-disable-line

  const wijzig = async (id: string, nieuw: string, huidig: string) => {
    if (nieuw === huidig) return;
    if (nieuw === 'blocked' && !window.confirm('Lid blokkeren? De SEPA-machtiging wordt ingetrokken; het lid moet die vernieuwen.')) return;
    setBusy(id);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/members/${id}/${ACTIE[nieuw]}`, { method: 'POST', headers });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Mislukt'); }
      notify.success(nieuw === 'active' ? 'Lid op actief gezet.' : nieuw === 'suspended' ? 'Lid geschorst.' : 'Lid geblokkeerd — SEPA-vernieuwing gevraagd.', 'Toegang gewijzigd');
      load();
    } catch (e: any) { notify.error(e.message || 'Mislukt'); } finally { setBusy(''); }
  };

  const goedkeuren = async (id: string) => {
    setBusy(id);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/members/${id}/approve`, { method: 'POST', headers });
      if (!r.ok) throw new Error();
      notify.success('Aanmelding goedgekeurd — lid is actief.'); load();
    } catch { notify.error('Goedkeuren mislukt.'); } finally { setBusy(''); }
  };

  const aanmeldingen = rows.filter((m) => m.status === 'inactive');

  return (
    <Layout>
      <div className="mb-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold text-mfb-blue">Leden</h1>
          <p className="text-sm text-slate-500">Leden per collectief: bekijk de kaart, keur aanmeldingen goed en regel de toegang per lid.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={cid} onChange={(e) => setCid(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
            {colls.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={() => setShowInvite(true)} className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600">+ Lid uitnodigen</button>
        </div>
      </div>

      {aanmeldingen.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <strong>{aanmeldingen.length} aanmelding(en)</strong> wachten op goedkeuring — keur ze hieronder goed.
        </div>
      )}

      {showInvite && cid && <InviteForm cid={cid} token={accessToken!} onClose={() => { setShowInvite(false); load(); }} />}

      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">Geen leden in dit collectief.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50"><tr>
              {['Lid', 'E-mail', 'SEPA-machtiging', 'Status', 'Toegang / actie'].map((h) => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((m) => {
                const st = STATUS[m.status] || { label: m.status, cls: 'bg-slate-100 text-slate-600' };
                return (
                  <tr key={m.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <button onClick={() => navigate(`/backoffice/members/${m.id}`)} className="font-medium text-mfb-blue hover:underline">{m.company_name}<span className="text-mfb-orange ml-2">→</span></button>
                      <div className="text-xs text-slate-400">{m.full_name}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{m.email}</td>
                    <td className="px-5 py-3">
                      {m.machtiging_actief ? <span className="text-xs font-medium text-emerald-700">Actief</span> : <span className="text-xs font-medium text-amber-700">Geen / te vernieuwen</span>}
                    </td>
                    <td className="px-5 py-3"><span className={`px-3 py-1 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span></td>
                    <td className="px-5 py-3">
                      {m.status === 'inactive' ? (
                        <button disabled={busy === m.id} onClick={() => goedkeuren(m.id)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">Goedkeuren</button>
                      ) : (
                        <select disabled={busy === m.id} value={['active', 'suspended', 'blocked'].includes(m.status) ? m.status : ''}
                          onChange={(e) => wijzig(m.id, e.target.value, m.status)}
                          className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white">
                          <option value="active">Actief</option>
                          <option value="suspended">Geschorst</option>
                          <option value="blocked">Geblokkeerd</option>
                        </select>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}
    </Layout>
  );
};

export default AdminLedenPage;
